import React, { useState } from "react";
import { Modal, ModalHeader } from "../../globalComponents/ui/Modal";
import { PrimaryButton, SecondaryButton } from "../../globalComponents/ui/Button";
import {
  SettingsModalBody, SettingsModalFooter,
  SettingsFormGroup, SettingsFormLabel, SettingsFormSelect,
} from "../Settings/SettingShell";
import { uploadFileMetadata, createDocument } from "../../_services/documentsService";

const emptyState = { financialYearId: "", files: [] };

const DocumentUploadModal = ({ show, onClose, onSuccess, financialYears, financialYearId: defaultFyId }) => {
  const [form, setForm] = useState({ ...emptyState, financialYearId: defaultFyId ?? "" });
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const canSubmit = form.financialYearId && form.files.length > 0;

  const handleClose = () => {
    if (uploading) return;
    setForm({ ...emptyState, financialYearId: defaultFyId ?? "" });
    setError("");
    onClose();
  };

  const handleFilesPicked = (fileList) => {
    setForm((f) => ({ ...f, files: [...f.files, ...Array.from(fileList)] }));
  };

  const removeFile = (idx) => {
    setForm((f) => ({ ...f, files: f.files.filter((_, i) => i !== idx) }));
  };

  const handleUpload = async () => {
    if (!canSubmit) return;
    setUploading(true);
    setError("");
    try {
      const outcomes = await Promise.all(form.files.map(async (file) => {
        try {
          const { fileMetadataId, fileMetadata } = await uploadFileMetadata(file);
          if (!fileMetadataId) return { file, ok: false };
          const document = await createDocument({
            documentType: "ESG_DOCUMENT_LIBRARY",
            financialYearId: Number(form.financialYearId),
            fileName: fileMetadata?.fileName ?? file.name,
            fileMetadataId,
            shouldParse: false,
          });
          return { file, ok: !!document };
        } catch {
          return { file, ok: false };
        }
      }));

      const failed = outcomes.filter((o) => !o.ok);
      if (failed.length) {
        setError(
          failed.length === outcomes.length
            ? "Upload failed. Please try again."
            : `${failed.length} of ${outcomes.length} file(s) failed to upload: ${failed.map((o) => o.file.name).join(", ")}`
        );
        setForm((f) => ({ ...f, files: failed.map((o) => o.file) }));
        if (failed.length < outcomes.length) onSuccess();
        return;
      }

      onSuccess();
      handleClose();
    } finally {
      setUploading(false);
    }
  };

  return (
    <Modal show={show} onClose={handleClose} maxWidth={560}>
      <ModalHeader icon="fas fa-upload" title="Upload Policy Document" subtitle="Upload one or more policy documents for a financial year" onClose={handleClose} />
      <SettingsModalBody>
        <SettingsFormGroup>
          <SettingsFormLabel>Financial Year *</SettingsFormLabel>
          <SettingsFormSelect
            value={form.financialYearId}
            onChange={(e) => {
              const value = e.target.value;
              setForm((f) => ({ ...f, financialYearId: value }));
            }}
            disabled={uploading}
          >
            <option value="">Select Financial Year</option>
            {financialYears.map((fy) => (
              <option key={fy.id} value={fy.id}>{fy.financial_year_value}</option>
            ))}
          </SettingsFormSelect>
        </SettingsFormGroup>

        <SettingsFormGroup style={{ marginTop: 12 }}>
          <SettingsFormLabel>Files *</SettingsFormLabel>
          <div
            onClick={() => document.getElementById("esg-document-input")?.click()}
            style={{ border: "2px dashed #3f88a5", borderRadius: 10, padding: "22px 16px", textAlign: "center", cursor: "pointer", background: "#f8fbfc" }}
          >
            <i className="fas fa-cloud-upload-alt" style={{ fontSize: 20, color: "#3f88a5" }} />
            <div style={{ fontSize: 13, fontWeight: 600, color: "#3f88a5", marginTop: 6 }}>Click to browse files</div>
            <input
              id="esg-document-input"
              type="file"
              multiple
              hidden
              disabled={uploading}
              onChange={(e) => e.target.files.length && handleFilesPicked(e.target.files)}
            />
          </div>

          {form.files.length > 0 && (
            <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
              {form.files.map((file, idx) => (
                <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, border: "1px solid #e2e8f0", borderRadius: 8, padding: "7px 12px" }}>
                  <i className="fas fa-file-alt" style={{ color: "#3f88a5" }} />
                  <span style={{ flex: 1, fontSize: 12.5, color: "#1e293b", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {file.name}
                  </span>
                  <button onClick={() => removeFile(idx)} disabled={uploading} style={{ border: "none", background: "none", cursor: "pointer", color: "#94a3b8" }}>
                    <i className="fas fa-times" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </SettingsFormGroup>

        {error && (
          <div style={{ marginTop: 12, padding: "10px 14px", borderRadius: 8, background: "#fee2e2", border: "1px solid #fca5a5", fontSize: 12, color: "#dc2626", fontWeight: 600 }}>
            {error}
          </div>
        )}
      </SettingsModalBody>
      <SettingsModalFooter>
        <SecondaryButton onClick={handleClose} disabled={uploading}>Cancel</SecondaryButton>
        <PrimaryButton onClick={handleUpload} disabled={!canSubmit || uploading}>
          {uploading ? "Uploading…" : `Upload ${form.files.length || ""} File${form.files.length === 1 ? "" : "s"}`}
        </PrimaryButton>
      </SettingsModalFooter>
    </Modal>
  );
};

export default DocumentUploadModal;
